import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone } from '@fortawesome/free-solid-svg-icons'
import { DisconnectionDetails, useConversation } from '@elevenlabs/react'
import { Orb, OrbStateBadge, type AgentState } from '@/uiV2/elevenLabs/orb'
import Note from '../common/note/Note'
import { NoteType } from '../../enums/NoteType'
import { Pages } from '../../Pages'
import Button from '../common/form/inputs/Button'
import {
    jsCapture,
    jsIdentify,
    setJsUserEmail,
    setJsUserId,
} from '../../jobseeker/jobseekerPosthog'
import { PersonDto } from '../../api/person/models/PersonDto'
import type { PersonNetworkImportStatusSummaryDto } from '../../api/person/PersonApi'
import authApi from '@/api/auth/AuthApi'
import kitApi from '@/api/kit/KitApi'
import ShareInviteCard from './components/ShareInviteCard'
import { useShareInvite } from './hooks/useShareInvite'
import useSpeakToKitPageTitle from './hooks/useSpeakToKitPageTitle'
import { speakToKitThemeVars } from './theme/speakToKitThemeVars'
import SpeakToKitLogo from './components/SpeakToKitLogo'
import LoadingSpinner from '../common/loading/LoadingSpinner'

const AGENT_ID = 'agent_8701kch82t3cehcsd0y3gty9rxfd'

const containerClass = 'mx-auto w-full max-w-[1200px] px-6'

export default function CandidateInterview(): React.JSX.Element | null {
    useSpeakToKitPageTitle('Interview')
    const navigate = useNavigate()
    const [error, setError] = useState<string | null>(null)
    const [isStarting, setIsStarting] = useState(false)
    const [isStopping, setIsStopping] = useState(false)
    const [conversationMode, setConversationMode] = useState<
        'speaking' | 'listening' | null
    >(null)
    const [hasAutoStarted, setHasAutoStarted] = useState(false)
    const [hasConnectedOnce, setHasConnectedOnce] = useState(false)
    const [showThankYou, setShowThankYou] = useState(false)
    const hasConnectedOnceRef = useRef(false)
    const connectStartTimeRef = useRef<number | null>(null)
    const callStartTimeRef = useRef<number | null>(null)
    const conversationIdRef = useRef<string | null>(null)
    const hasCallStartedRef = useRef(false)
    const identifiedUserRef = useRef<string | null>(null)
    const [sessionInfo, setSessionInfo] = useState<{
        user: {
            id: string
            email: string
            name: string
            referralCode?: string
        }
        person_profile: PersonDto | null
        personNetworkImportStatus: PersonNetworkImportStatusSummaryDto | null
    } | null>(null)
    const [loadingSession, setLoadingSession] = useState(true)
    const buildShareInviteMessage = useCallback((shareUrl: string) => {
        return `I just spoke to Kit. It's a talent agent designed to work for you and get past the usual CV filtering tools. Very good. You might find it helpful too.\n\n${shareUrl}`
    }, [])
    const trackInviteCopied = useCallback(() => {
        jsCapture('js_url_copied')
    }, [])
    const trackInviteShared = useCallback(() => {
        jsCapture('js_share_clicked')
    }, [])

    const referralCode = sessionInfo?.user?.referralCode?.trim() ?? ''
    const personalizeShareUrl = useCallback(
        (baseUrl: string) => {
            if (!referralCode) {
                return baseUrl
            }
            try {
                const origin =
                    typeof window !== 'undefined'
                        ? window.location?.origin
                        : undefined
                const url = new URL(
                    baseUrl,
                    baseUrl.startsWith('http') ? undefined : origin,
                )
                url.searchParams.set('ref', referralCode)
                return url.toString()
            } catch {
                const separator = baseUrl.includes('?') ? '&' : '?'
                return `${baseUrl}${separator}ref=${encodeURIComponent(referralCode)}`
            }
        },
        [referralCode],
    )

    const {
        shareInviteUrl,
        whatsAppShareUrl,
        isMobileShareAvailable,
        isDesktopViewport,
        hasCopiedLink,
        handleCopyInvite,
        handleShareInvite,
    } = useShareInvite({
        landingPath: Pages.CandidateLandingOption1,
        messageBuilder: buildShareInviteMessage,
        onCopy: trackInviteCopied,
        onShare: trackInviteShared,
        transformShareUrl: personalizeShareUrl,
    })

    const { startSession, endSession, status, getOutputVolume } =
        useConversation({
            onConnect: ({ conversationId }) => {
                setHasConnectedOnce(true)
                hasConnectedOnceRef.current = true
                setShowThankYou(false)
                setError(null)
                conversationIdRef.current = conversationId || null

                const connectStart = connectStartTimeRef.current
                const connectTimeMs =
                    typeof connectStart === 'number'
                        ? Math.max(0, Date.now() - connectStart)
                        : undefined

                jsCapture('js_voice_connected', {
                    agent_id: AGENT_ID,
                    connect_time_ms: connectTimeMs,
                })

                if (!hasCallStartedRef.current) {
                    callStartTimeRef.current = Date.now()
                    hasCallStartedRef.current = true
                    jsCapture('js_call_started', {
                        call_id: conversationIdRef.current ?? undefined,
                    })
                }
            },
            onDisconnect: (details: DisconnectionDetails) => {
                const disconnectMessage =
                    details?.reason === 'error'
                        ? details?.context instanceof CloseEvent
                            ? details.context.reason ||
                              'Unable to connect to Kit.'
                            : 'Unable to connect to Kit.'
                        : null
                const endedConversationId =
                    conversationIdRef.current?.trim() || null

                if (endedConversationId) {
                    void kitApi
                        .markConvoCallEnded({
                            convId: endedConversationId,
                        })
                        .catch((kitError) => {
                            console.error(
                                'Failed to mark Kit conversation as call ended for candidate',
                                {
                                    convId: endedConversationId,
                                    kitError,
                                },
                            )
                        })
                }

                if (!hasConnectedOnceRef.current && disconnectMessage) {
                    setError(disconnectMessage)
                }
                if (hasConnectedOnceRef.current) {
                    setShowThankYou(true)
                }

                const durationSeconds =
                    callStartTimeRef.current !== null
                        ? Math.max(
                              0,
                              Math.round(
                                  (Date.now() - callStartTimeRef.current) /
                                      1000,
                              ),
                          )
                        : undefined
                const endReason =
                    details?.reason === 'user'
                        ? 'hangup_by_user'
                        : details?.reason === 'agent'
                          ? 'agent_finished'
                          : details?.reason === 'error'
                            ? 'error'
                            : 'disconnect'

                if (hasCallStartedRef.current) {
                    jsCapture('js_call_ended', {
                        call_id: conversationIdRef.current ?? undefined,
                        duration_s: durationSeconds,
                        end_reason: endReason,
                        completed: details?.reason === 'agent',
                    })
                    hasCallStartedRef.current = false
                    callStartTimeRef.current = null
                }

                setConversationMode(null)
            },
            onModeChange: ({ mode }) => {
                setConversationMode(mode)
            },
            onError: (message) => {
                setError(message || 'Something went wrong starting the call.')
            },
        })

    useEffect(() => {
        jsCapture('js_interview_viewed')
        // jsCapture is a module import and stable
    }, [])

    useEffect(() => {
        let active = true

        const loadSessionUser = async () => {
            try {
                const res = await authApi.getMeDetails()
                if (!active) return

                if (!res.user) {
                    navigate(Pages.CandidateSignUp, { replace: true })
                    return
                }

                setSessionInfo({
                    user: {
                        id: res.user.id,
                        email: res.user.email,
                        name: res.user.name?.trim() || res.user.email,
                        referralCode: res.user.referralCode,
                    },
                    person_profile: res.person ?? null,
                    personNetworkImportStatus:
                        res.personNetworkImportStatus ?? null,
                })
            } catch (err) {
                console.error('Failed to load job seeker session', err)
                if (active) {
                    navigate(Pages.CandidateSignUp, { replace: true })
                }
            } finally {
                if (active) {
                    setLoadingSession(false)
                }
            }
        }

        loadSessionUser()

        return () => {
            active = false
        }
    }, [navigate])

    useEffect(() => {
        if (!sessionInfo?.user) return
        if (identifiedUserRef.current === sessionInfo.user.id) return

        jsIdentify(sessionInfo.user.id, {
            email: sessionInfo.user.email,
        })
        setJsUserId(sessionInfo.user.id)
        setJsUserEmail(sessionInfo.user.email)
        identifiedUserRef.current = sessionInfo.user.id
    }, [sessionInfo])

    useEffect(() => {
        hasConnectedOnceRef.current = hasConnectedOnce
    }, [hasConnectedOnce])

    useEffect(() => {
        if (status === 'disconnected' && hasConnectedOnce) {
            setShowThankYou(true)
        }
    }, [hasConnectedOnce, status])

    useEffect(() => {
        return () => {
            endSession().catch((endError) => {
                console.error('Failed to end ElevenLabs session on unmount', {
                    endError,
                })
            })
        }
        // We only want to run this cleanup on unmount; dependency here would
        // cause the cleanup to fire on every render if endSession changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleStart = useCallback(async () => {
        if (!sessionInfo?.user) return

        setIsStarting(true)
        setError(null)
        setShowThankYou(false)
        hasCallStartedRef.current = false
        connectStartTimeRef.current = Date.now()
        callStartTimeRef.current = null
        setConversationMode(null)
        conversationIdRef.current = null

        jsCapture('js_voice_connect_attempted', { agent_id: AGENT_ID })

        try {
            await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch (permissionError) {
            console.error(
                'Microphone permission was denied for ElevenLabs conversation',
                permissionError,
            )
            setError(
                'We need permission to use your microphone before starting the call.',
            )
            setIsStarting(false)
            return
        }

        try {
            const personImportStatus =
                sessionInfo.personNetworkImportStatus ?? null
            const personImportCompleted =
                personImportStatus?.status === 'COMPLETE'

            const dynamicVariables: Record<string, string | boolean> = {
                user_id: sessionInfo.user.id,
                name: sessionInfo.user.name,
                person_import_complete: personImportCompleted,
            }

            if (sessionInfo.person_profile) {
                dynamicVariables.person_profile = JSON.stringify(
                    sessionInfo.person_profile,
                )
            }

            if (
                !personImportCompleted &&
                personImportStatus?.status === 'IN_PROGRESS' &&
                personImportStatus.statusId
            ) {
                dynamicVariables.person_import_id = personImportStatus.statusId
            }

            const conversationId = await startSession({
                agentId: AGENT_ID,
                connectionType: 'webrtc',
                userId: sessionInfo.user.id,
                dynamicVariables,
            })
            if (conversationId) {
                conversationIdRef.current = conversationId
                void kitApi
                    .startConvoSelf({
                        convId: conversationId,
                        personId: sessionInfo.person_profile?.id,
                        dynamicVariables,
                        agentId: AGENT_ID,
                        source: 'network_kit',
                    })
                    .catch((kitError) => {
                        console.error(
                            'Failed to log Kit conversation for candidate',
                            kitError,
                        )
                    })
            }
        } catch (startError) {
            console.error('Failed to start ElevenLabs conversation', startError)
            const message =
                startError instanceof Error
                    ? startError.message
                    : 'Unable to start the call right now.'
            setError(message)
        } finally {
            setIsStarting(false)
        }
        // jsCapture is stable
    }, [sessionInfo, startSession])

    useEffect(() => {
        if (hasAutoStarted) return
        if (!sessionInfo?.user) return
        if (showThankYou) return
        if (status !== 'disconnected') return

        setHasAutoStarted(true)
        void handleStart()
    }, [handleStart, hasAutoStarted, sessionInfo, showThankYou, status])

    const handleEnd = useCallback(async () => {
        setIsStopping(true)
        setError(null)
        setShowThankYou(false)

        try {
            await endSession()
        } catch (stopError) {
            console.error('Failed to end ElevenLabs conversation', stopError)
            const message =
                stopError instanceof Error
                    ? stopError.message
                    : 'Unable to end the call. Please try again.'
            setError(message)
        } finally {
            setIsStopping(false)
        }
    }, [endSession])

    const agentState: AgentState = useMemo(() => {
        if (status !== 'connected') return null
        if (conversationMode === 'speaking') return 'talking'
        if (conversationMode === 'listening') return 'listening'
        return null
    }, [conversationMode, status])
    const isAutoConnecting =
        !hasAutoStarted &&
        Boolean(sessionInfo?.user) &&
        !showThankYou &&
        status === 'disconnected'
    const isConnecting = status === 'connecting' || isStarting
    const isActiveCall =
        status === 'connecting' ||
        status === 'connected' ||
        status === 'disconnecting' ||
        isStarting ||
        isAutoConnecting
    const canStart =
        status === 'disconnected' &&
        !isStarting &&
        !showThankYou &&
        Boolean(sessionInfo?.user)
    const shouldShowStartButton =
        !isActiveCall && !showThankYou && (hasAutoStarted || Boolean(error))
    const showHangUp = status === 'connected' || status === 'disconnecting'
    const canHangUp = status === 'connected' && !isStopping
    const shouldShowOrb = isActiveCall

    if (loadingSession) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-gray-50"
                style={speakToKitThemeVars}
            >
                <LoadingSpinner />
            </div>
        )
    }

    if (!sessionInfo) {
        return null
    }

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={speakToKitThemeVars}
        >
            <header className="w-full py-6">
                <div
                    className={`${containerClass} flex items-center justify-center`}
                >
                    <SpeakToKitLogo to={Pages.CandidateLandingOption2} />
                </div>
            </header>

            <main className="flex-1">
                <section className="py-8 md:py-12">
                    <div className={containerClass}>
                        <div className="mx-auto flex w-full max-w-3xl flex-col space-y-8 lg:space-y-10">
                            {showThankYou ? (
                                <ShareInviteCard
                                    shareInviteUrl={shareInviteUrl}
                                    whatsAppShareUrl={whatsAppShareUrl}
                                    isMobileShareAvailable={
                                        isMobileShareAvailable
                                    }
                                    isDesktopViewport={isDesktopViewport}
                                    hasCopiedLink={hasCopiedLink}
                                    onCopy={handleCopyInvite}
                                    onShare={handleShareInvite}
                                />
                            ) : null}

                            {!showThankYou || isActiveCall ? (
                                <div className="relative rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                                    <div className="space-y-4">
                                        {error && !hasConnectedOnce ? (
                                            <Note
                                                type={NoteType.ERROR}
                                                containerClassNames="w-full border border-red-100 bg-red-50/80"
                                            >
                                                {error}
                                            </Note>
                                        ) : null}

                                        {shouldShowStartButton ? (
                                            <Button
                                                type="button"
                                                style="primary"
                                                enabled={canStart}
                                                classes="w-full justify-center gap-2 py-3 text-base shadow-lg shadow-ctxt-blue/20 hover:shadow-xl transition-all"
                                                onClick={handleStart}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPhone}
                                                />
                                                {isConnecting
                                                    ? 'Connecting…'
                                                    : 'Speak to Kit'}
                                            </Button>
                                        ) : null}

                                        {shouldShowOrb ? (
                                            <div className="flex w-full flex-col items-center gap-4">
                                                <div className="w-full rounded-2xl border border-border bg-secondary/50 p-4">
                                                    <div className="mx-auto aspect-square max-w-48">
                                                        {status ===
                                                        'connected' ? (
                                                            <Orb
                                                                className="h-full w-full"
                                                                agentState={
                                                                    agentState
                                                                }
                                                                volumeMode={
                                                                    status ===
                                                                    'connected'
                                                                        ? 'manual'
                                                                        : 'auto'
                                                                }
                                                                getOutputVolume={
                                                                    status ===
                                                                    'connected'
                                                                        ? getOutputVolume
                                                                        : undefined
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                                                                Connecting to
                                                                Kit…
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <OrbStateBadge
                                                    agentState={agentState}
                                                />
                                                {showHangUp ? (
                                                    <Button
                                                        type="button"
                                                        style="mild-danger"
                                                        enabled={canHangUp}
                                                        classes="min-w-[140px]"
                                                        onClick={handleEnd}
                                                    >
                                                        {isStopping ||
                                                        status ===
                                                            'disconnecting'
                                                            ? 'Ending…'
                                                            : 'Hang up'}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        {!shouldShowOrb && isActiveCall ? (
                                            <p className="text-sm text-muted-foreground">
                                                Connecting to Kit…
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    )
}
